const isRecord = (v: unknown): v is Record<string, any> => v !== null && typeof v === 'object' && !Array.isArray(v);
const forbidden = new Set(['__proto__', 'constructor', 'prototype']);

export interface LeadFieldDefinition { key: string; label?: string; type?: string; acquisition_aliases?: string[] }

/** Exact keys/explicit aliases only; ambiguous mappings leave acquisition untouched. */
export function resolveLeadData(metadata: unknown, facade?: unknown, definitions: LeadFieldDefinition[] = [], native?: unknown) {
  const source = isRecord(metadata) ? metadata : {};
  const canonical = Object.assign(Object.create(null), isRecord(source.custom_fields) ? source.custom_fields : {}, isRecord(facade) ? facade : {});
  // Native columns are authoritative by presence, including explicit clears.
  const nativeKeys = ['nome', 'telefone', 'email', 'cidade', 'regiao', 'produto_interesse', 'produto_id', 'valor', 'observacoes', 'dados_qualificacao', 'motivo_desqualificacao', 'proxima_visita_at'];
  const present = (key: string) => Object.prototype.hasOwnProperty.call(canonical, key) ||
    (nativeKeys.includes(key) && isRecord(native) && Object.prototype.hasOwnProperty.call(native, key));
  const original = isRecord(source.meta_ads?.custom_fields) ? source.meta_ads.custom_fields : {};
  const acquisitionKeys: Record<string, string[]> = Object.create(null);
  const acquisition: Record<string, unknown> = Object.create(null);
  const targets = new Map<string, Set<string>>();
  for (const field of definitions) {
    if (!field || typeof field.key !== 'string' || forbidden.has(field.key)) continue;
    for (const alias of [field.key, ...(Array.isArray(field.acquisition_aliases) ? field.acquisition_aliases : [])]) {
      if (typeof alias !== 'string' || !alias || forbidden.has(alias)) continue;
      const keys = targets.get(alias) || new Set<string>(); keys.add(field.key); targets.set(alias, keys);
    }
  }
  const mapped = new Map<string, Array<[string, unknown]>>();
  for (const [key, value] of Object.entries(original)) {
    if (forbidden.has(key)) continue;
    const keys = targets.get(key);
    const target = keys?.size === 1 ? [...keys][0] : key;
    if (present(key) || (keys?.size === 1 && present(target))) continue;
    const entries = mapped.get(target) || []; entries.push([key, value]); mapped.set(target, entries);
  }
  for (const [target, entries] of mapped) {
    // Multiple original questions claiming one field are not resolved by order.
    const add = (key: string, originalKey: string, value: unknown) => {
      acquisition[key] = value;
      // Classification belongs to consumers; preserve original and all possible destinations.
      acquisitionKeys[key] = [originalKey, ...(targets.get(originalKey) || [])];
    };
    if (entries.length === 1) add(target, entries[0][0], entries[0][1]);
    else for (const [key, value] of entries) add(key, key, value);
  }
  const current: Record<string, unknown> = Object.create(null);
  for (const [key, value] of Object.entries(canonical)) if (!forbidden.has(key)) current[key] = value;
  return { current, acquisition, effective: { ...current, ...acquisition }, original, acquisitionKeys };
}

export function displayLeadValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return JSON.stringify(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function customFieldsRevision(metadata: unknown): string {
  return JSON.stringify(metadata ?? null);
}

/** A single operational projection; originals remain evidence, never editable fields. */
export function leadDataPresentation(metadata: unknown, facade?: unknown, definitions: LeadFieldDefinition[] = [], native?: unknown) {
  const resolved = resolveLeadData(metadata, facade, definitions, native);
  const keys = [...new Set([...definitions.map(f => f.key), ...Object.keys(resolved.current)])];
  const rows = keys.map(key => {
    const matches = definitions.filter(f => f.key === key);
    const field = matches.length === 1 ? matches[0] : undefined;
    const originals = Object.entries(resolved.original).filter(([question]) => {
      const matches = definitions.filter(f => f.key === question || f.acquisition_aliases?.includes(question));
      return matches.length === 1 && matches[0].key === key;
    });
    const value = resolved.effective[key];
    return { key, label: field?.label || key.replace(/_/g, ' '), value,
      editable: !!field && ['text', 'number', 'boolean', 'date'].includes(field.type || '') && !Array.isArray(value) && !isRecord(value),
      source: Object.hasOwn(resolved.current, key) ? 'Origem não registrada' : Object.hasOwn(resolved.acquisition, key) ? 'Formulário' : null,
      originals };
  });
  const linked = new Set(rows.flatMap(row => row.originals.map(([key]) => key)));
  return { rows, unmapped: Object.entries(resolved.original).filter(([key]) => !linked.has(key)) };
}
